import sortChannels from './sortChannels'

export const ADMIN_DELETED_LABEL = 'Mensaje eliminado por administrador'

export const ADMIN_DELETED_TEXT = `_${ADMIN_DELETED_LABEL}_`

export const hasServerId = message => message?.id !== undefined && message?.id !== null

export const generateClientId = () => {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : undefined
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID()
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

const withoutChannelId = ({ channel_id, ...message }) => message

const messageKey = message => {
  if (hasServerId(message)) return `id:${message.id}`
  if (message?.client_id) return `client:${message.client_id}`
  return null
}

const isAdminDeleted = message => message?.text === ADMIN_DELETED_TEXT

const isKeyless = message => !hasServerId(message) && !message.client_id

const hasDate = message => message?.date !== undefined && message?.date !== null

const instantOf = date => {
  if (date === undefined || date === null) return date
  const time = new Date(date).getTime()
  return Number.isNaN(time) ? date : time
}

const isSameMessageWithoutKeys = (candidate, message) => {
  if (candidate.user_id !== message.user_id) return false
  if (instantOf(candidate.date) !== instantOf(message.date)) return false
  if (candidate.text === message.text) return true
  return hasDate(message) && hasServerId(candidate) && isAdminDeleted(candidate)
}

const isShownWithoutKeys = (messages, message) =>
  messages.some(candidate => !candidate.pending && isSameMessageWithoutKeys(candidate, message))

export const upsertReceivedMessage = (messages, payload) => {
  const list = messages || []
  const message = withoutChannelId(payload || {})
  if (hasServerId(message) && list.some(candidate => candidate.id === message.id)) return list
  if (isKeyless(message) && hasDate(message) && isShownWithoutKeys(list, message)) return list
  return [...list, message]
}

const findSendOkIndex = (messages, serverMessage) =>
  serverMessage.client_id
    ? messages.findIndex(
        candidate => !hasServerId(candidate) && candidate.client_id === serverMessage.client_id
      )
    : messages.findIndex(
        candidate =>
          !hasServerId(candidate) &&
          candidate.pending &&
          candidate.user_id === serverMessage.user_id &&
          candidate.text === serverMessage.text
      )

export const applySendOk = (messages, payload) => {
  const list = messages || []
  const serverMessage = withoutChannelId(payload || {})
  const index = findSendOkIndex(list, serverMessage)
  const alreadyStored =
    hasServerId(serverMessage) && list.some(candidate => candidate.id === serverMessage.id)

  if (index === -1) return alreadyStored ? list : [...list, serverMessage]
  if (alreadyStored) return list.filter((_, position) => position !== index)
  return list.map((candidate, position) => (position === index ? serverMessage : candidate))
}

export const applySendError = (messages, payload) => {
  const list = messages || []
  const clientId = payload?.client_id
  if (!clientId) return list
  return list.map(candidate =>
    !hasServerId(candidate) && candidate.client_id === clientId
      ? { ...candidate, pending: false, failed: true, error: payload.error || null }
      : candidate
  )
}

export const applyDeletedMessage = (messages, messageId) => {
  const list = messages || []
  const index = list.findIndex(candidate => hasServerId(candidate) && candidate.id === messageId)
  if (index === -1) return list

  return list.map((candidate, position) => {
    let next = candidate
    if (position === index) {
      const { kind, media, location, caption, reply_to, ...rest } = candidate
      next = { ...rest, text: ADMIN_DELETED_TEXT }
    }
    if (next.reply_to && next.reply_to.id === messageId) {
      const { thumbnail_url, ...quote } = next.reply_to
      next = { ...next, reply_to: { ...quote, kind: 'text', text: ADMIN_DELETED_LABEL } }
    }
    return next
  })
}

export const mergeServerMessages = (serverMessages, localMessages) => {
  const server = serverMessages || []
  const known = new Set()
  server.forEach(message => {
    if (hasServerId(message)) known.add(`id:${message.id}`)
    if (message.client_id) known.add(`client:${message.client_id}`)
  })
  const extras = (localMessages || []).filter(message =>
    isKeyless(message) ? !isShownWithoutKeys(server, message) : !known.has(messageKey(message))
  )
  return [...server, ...extras]
}

const findPendingIndex = (messages, clientId) =>
  clientId ? messages.findIndex(candidate => !hasServerId(candidate) && candidate.client_id === clientId) : -1

const findStoredCopy = (messages, id) =>
  messages.find(candidate => hasServerId(candidate) && candidate.id === id)

const replaceAt = (messages, index, message) =>
  messages[index] === message
    ? messages
    : messages.map((candidate, position) => (position === index ? message : candidate))

const mergeStoredMessage = (messages, message) => {
  const stored = findStoredCopy(messages, message.id)
  if (!stored) return applySendOk(messages, message)
  const deletedOnlyInList = isAdminDeleted(message) && !isAdminDeleted(stored)
  return deletedOnlyInList ? applyDeletedMessage(messages, message.id) : messages
}

const mergeKeylessMessage = (messages, message) => {
  if (isShownWithoutKeys(messages, message)) return messages
  const sendOkIndex = findSendOkIndex(messages, message)
  return sendOkIndex === -1 ? [...messages, message] : replaceAt(messages, sendOkIndex, message)
}

const mergeChannelListMessage = (messages, message) => {
  const pendingIndex = findPendingIndex(messages, message.client_id)
  if (pendingIndex !== -1) {
    return hasServerId(message) ? applySendOk(messages, message) : replaceAt(messages, pendingIndex, message)
  }
  if (hasServerId(message)) return mergeStoredMessage(messages, message)
  if (isKeyless(message)) return mergeKeylessMessage(messages, message)
  const alreadyShown = messages.some(candidate => candidate.client_id === message.client_id)
  return alreadyShown ? messages : [...messages, message]
}

export const reconcileLocalMessages = (localMessages, channelMessages) => {
  if (!localMessages || !channelMessages) return localMessages || channelMessages
  return channelMessages.reduce(mergeChannelListMessage, localMessages)
}

const isUnknownServerMessage = (localMessages, message) => {
  if (hasServerId(message)) {
    return !findStoredCopy(localMessages, message.id) && findSendOkIndex(localMessages, message) === -1
  }
  return (
    isKeyless(message) &&
    !isShownWithoutKeys(localMessages, message) &&
    findSendOkIndex(localMessages, message) === -1
  )
}

export const hasNewServerMessages = (localMessages, channelMessages) =>
  Boolean(localMessages) &&
  (channelMessages || []).some(message => isUnknownServerMessage(localMessages, message))

export const markMessagesRead = (messages, userId) =>
  (messages || []).map(message => {
    const readBy = message.read_by || []
    return readBy.includes(userId) ? message : { ...message, read_by: [...readBy, userId] }
  })

export const upsertChannelMessages = (channels, channel, update) => {
  const list = channels || []
  if (!channel) return list
  if (!list.some(candidate => candidate._id === channel._id)) {
    return [...list, { ...channel, messages: update(channel.messages || []) }]
  }
  return list.map(candidate =>
    candidate._id === channel._id ? { ...candidate, messages: update(candidate.messages || []) } : candidate
  )
}

export const updateChannelMessages = (channels, channelId, update) => {
  if (!channels || !channels.some(candidate => candidate._id === channelId)) return channels
  return channels.map(candidate =>
    candidate._id === channelId ? { ...candidate, messages: update(candidate.messages || []) } : candidate
  )
}

export const updateChannelList = (channelId, update) => channels => {
  const updated = updateChannelMessages(channels, channelId, update)
  return updated === channels ? channels : sortChannels(updated)
}
